import { between, clamp01, headingOf, turnToward, wrapAngle, type CritterWorld, type Random } from './critter-math.js';
import { restingRabbit, type RabbitAction, type RabbitMood } from './rabbit-model.js';

/** A rabbit is drawn at this multiple of its real size so it reads from the isometric camera. */
export const RABBIT_SCALE = 1.45;
/** Metres a rabbit notices something worth fleeing at, and how far it runs before it stops looking back. */
const SCARE_REACH = 6.5, SAFE_REACH = 12;
/** Where a hop's spring and landing fall within its 0–1 phase (see `hopPose`). */
const LIFT_OFF = .14, TOUCH_DOWN = .72;
const FORAGE = { length: .55, period: .62 }, BOLT = { length: 1.45, period: .4 };
const HOME_RADIUS = 6;

/**
 * A rabbit's wits: it sits and twitches, grooms, grazes and watches; now and then it hops a few lengths about its home
 * ground; and when something comes close it bolts away from it in long bounds, then sits bolt upright to look about.
 * The brain only decides where it is and what it is doing; `RabbitModel` turns the action into joints.
 */
export class RabbitBrain {
  readonly action: RabbitAction = restingRabbit();
  mode: 'sit' | 'hop' = 'sit';
  private sitTime = 0; private sitLength: number;
  private hop = { fromX: 0, fromZ: 0, heading: 0, length: 0, period: 1, bolt: 0 };
  private hopT = 0; private hopsLeft = 0;
  private goal: { x: number; z: number } | null = null;
  private bolting = false; private boltTime = 0; private alarm = 0;
  private fear: { x: number; z: number } | null = null;
  /** 0 by day, 1 deep in the night: a rabbit that is drowsy curls up to sleep instead of foraging. */
  drowsy = 0;

  constructor(public x: number, public z: number, public heading: number, private home: { x: number; z: number }, private random: Random) {
    this.action.idle = random() * 40; this.sitLength = between(random, 1, 4);
  }

  update(dt: number, world: CritterWorld) {
    const a = this.action;
    a.idle += dt; a.moodTime += dt; this.alarm = Math.max(0, this.alarm - dt);
    const threat = world.threat(this.x, this.z, this.bolting ? SAFE_REACH : SCARE_REACH);
    if (threat) { this.alarm = 3; this.bolting = true; this.boltTime = 0; this.fear = { x: threat.x, z: threat.z }; }
    else if (this.bolting && this.mode === 'sit') this.bolting = false;
    a.settle += ((this.mode === 'sit' ? 1 : 0) - a.settle) * (1 - Math.exp(-dt * (this.mode === 'sit' ? 7 : 18)));
    a.bolt += ((this.bolting ? 1 : 0) - a.bolt) * (1 - Math.exp(-dt * 10));
    if (this.mode === 'sit') this.sit(dt, world, threat);
    else this.hopping(dt, world, threat);
  }

  /** Where it is running from while it bolts, so rabbits nearby can bolt the same way. */
  get fleeingFrom() { return this.bolting ? this.fear : null; }

  private sit(dt: number, world: CritterWorld, threat: { x: number; z: number } | null) {
    this.sitTime += dt;
    if (threat) {
      const away = headingOf(this.x - threat.x, this.z - threat.z);
      this.heading = turnToward(this.heading, away, 14 * dt);
      if (this.startHop(world, away, BOLT, 1, .35)) return;
      this.setMood('alert'); return; // cornered: freeze, ears up
    }
    if (this.goal) {
      const toward = headingOf(this.goal.x - this.x, this.goal.z - this.z);
      this.heading = turnToward(this.heading, toward, 5 * dt);
      if (Math.abs(wrapAngle(this.heading - toward)) < .5 && this.startHop(world, toward, FORAGE, 0, .35)) return;
      if (this.sitTime > 1.5) this.goal = null; // a goal it cannot turn or hop toward is abandoned
      return;
    }
    if (this.sitTime < this.sitLength) return;
    this.sitTime = 0; this.sitLength = between(this.random, 1.6, 5.5);
    if (!this.alarm && this.drowsy < .6 && this.random() < .55) this.planForage(world);
    else this.setMood(this.pickMood());
  }

  private hopping(dt: number, world: CritterWorld, threat: { x: number; z: number } | null) {
    const a = this.action, h = this.hop;
    this.hopT += dt / h.period; this.boltTime += dt;
    // Only the airborne part of a hop covers ground.
    const covered = clamp01((this.hopT - LIFT_OFF) / (TOUCH_DOWN - LIFT_OFF));
    this.x = h.fromX + Math.sin(h.heading) * h.length * covered; this.z = h.fromZ + Math.cos(h.heading) * h.length * covered;
    this.heading = turnToward(this.heading, h.heading, 16 * dt);
    a.hop = Math.min(this.hopT, 1);
    if (this.hopT < 1) return;
    this.hopT -= 1; this.hopsLeft--;
    if (this.bolting && (threat || this.boltTime < 1.2) && this.boltTime < 6) {
      const away = threat ? headingOf(this.x - threat.x, this.z - threat.z) : h.heading;
      if (this.startHop(world, away, BOLT, 1, .35)) return;
    } else if (!this.bolting && this.hopsLeft > 0 && this.goal) {
      if (this.startHop(world, headingOf(this.goal.x - this.x, this.z - this.z), FORAGE, 0, .35)) return;
    }
    // Landed for good: settle, and after a bolt sit up and look.
    this.mode = 'sit'; this.hopT = 0; a.hop = 0; this.sitTime = 0; this.goal = null;
    if (this.bolting) { this.home = { x: this.x, z: this.z }; this.bolting = false; this.setMood('alert'); this.sitLength = between(this.random, 2.5, 4.5); }
    else this.setMood(this.pickMood());
  }

  /** Begins a hop at the preferred heading, or the nearest one that lands on open ground. */
  private startHop(world: CritterWorld, preferred: number, kind: { length: number; period: number }, bolt: number, margin: number) {
    for (const turn of [0, .45, -.45, .9, -.9, 1.5, -1.5]) {
      const heading = preferred + turn * (bolt ? 1 : .6);
      const length = kind.length * between(this.random, .9, 1.1), dx = Math.sin(heading), dz = Math.cos(heading);
      if (!world.free(this.x + dx * length, this.z + dz * length, margin) || !world.free(this.x + dx * length / 2, this.z + dz * length / 2, margin)) continue;
      this.hop = { fromX: this.x, fromZ: this.z, heading, length, period: kind.period * between(this.random, .92, 1.08), bolt };
      this.mode = 'hop'; this.hopT = 0; this.sitTime = 0; return true;
    }
    return false;
  }

  private planForage(world: CritterWorld) {
    for (let tries = 0; tries < 6; tries++) {
      const angle = this.random() * Math.PI * 2, reach = Math.sqrt(this.random()) * HOME_RADIUS;
      const goal = { x: this.home.x + Math.cos(angle) * reach, z: this.home.z + Math.sin(angle) * reach }, distance = Math.hypot(goal.x - this.x, goal.z - this.z);
      if (distance < 1 || !world.free(goal.x, goal.z, .4)) continue;
      this.goal = goal; this.hopsLeft = Math.min(8, Math.ceil(distance / FORAGE.length)); return;
    }
    this.setMood(this.pickMood());
  }

  private pickMood(): RabbitMood {
    if (this.alarm > 0) return 'alert';
    if (this.drowsy > .6) return 'sleep';
    const roll = this.random();
    return roll < .4 ? 'rest' : roll < .65 ? 'graze' : roll < .85 ? 'groom' : 'alert';
  }

  private setMood(mood: RabbitMood) {
    const a = this.action;
    if (mood === a.mood) return;
    a.previous = a.mood; a.mood = mood; a.moodTime = 0;
  }
}
